// --------------------------------------------------------------
// Created On: 2024-06-10
// Author: KC Willard
//
// Last Modified: 2025-03-26
// Modified By: Hannah Vaughan
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import Error500Page from "../Error500Page/Error500Page";
import Spinner from "../../components/Spinner/Spinner";
import useApi from "../../hooks/useApi";
import { API } from "../../constants/miscellaneous";
import SalesOrderDetails from "../../components/SalesOrderDetails/SalesOrderDetails";

// Page that displays a completed sales order's information immediately after placing the order.
export default function OrderCompletePage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const { orderNumber } = useParams();
  const [poNumber, setPoNumber] = useState<string>("");
  const [shippingCountry, setShippingCountry] = useState<string>("");
  const [shippingCompanyName, setShippingCompanyName] = useState<string>("");
  const [shippingAddressLine1, setShippingAddressLine1] = useState<string>("");
  const [shippingAddressLine2, setShippingAddressLine2] = useState<string>("");
  const [shippingContactName, setShippingContactName] = useState<string>("");
  const [shippingPhoneNumber, setShippingPhoneNumber] = useState<string>("");
  const [shippingCity, setShippingCity] = useState<string>("");
  const [shippingState, setShippingState] = useState<string>("");
  const [shippingZipCode, setShippingZipCode] = useState<string>("");
  const [specialInstructions, setSpecialInstructions] = useState<string>("");
  const [itemsSubtotal, setItemsSubtotal] = useState<string>("");
  const [shipBy, setShipBy] = useState<string>("");
  const [grandTotal, setGrandTotal] = useState<string>("");
  const [orderDate, setOrderDate] = useState<string>("");
  const [companyDiscount, setCompanyDiscount] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);

  // Get order information.
  useApi(
    () => {
      setLoading(true);
      return true;
    },
    {
      url: `${API}/completed-order/${orderNumber}`,
      method: "GET",
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        setPoNumber(responseBody.poNumber);
        setShippingCountry(responseBody.shippingCountry);
        setShippingCompanyName(responseBody.shippingCompanyName);
        setShippingAddressLine1(responseBody.shippingAddressLine1);
        setShippingAddressLine2(responseBody.shippingAddressLine2);
        setShippingContactName(responseBody.shippingContactName);
        setShippingPhoneNumber(responseBody.shippingPhoneNumber);
        setShippingCity(responseBody.shippingLocality);
        setShippingState(responseBody.shippingAdministrativeArea);
        setShippingZipCode(responseBody.shippingPostalCode);
        setSpecialInstructions(responseBody.specialInstructions);
        setShipBy(responseBody.shipBy);
        setGrandTotal(responseBody.grandTotal);
        setOrderDate(responseBody.orderDate);
        setItemsSubtotal(responseBody.itemsSubtotal);
        setCompanyDiscount(responseBody.discountPercent);
        setCompanyName(responseBody.companyName);
        setOrderItems(responseBody.orderItems);
        setFailedToLoad(false);
      } else {
        setFailedToLoad(true);
      }
      setLoading(false);
    },
    []
  );

  return failedToLoad ? (
    <Error500Page />
  ) : (
    <div className="container p-4">
      <Spinner loading={loading} />
      <div className="p-3">
        <SalesOrderDetails
          showHeadersAndFooters={true}
          orderNumber={orderNumber || ""}
          poNumber={poNumber}
          loading={loading}
          shippingCountry={shippingCountry}
          shippingCompanyName={shippingCompanyName}
          shippingAddressLine1={shippingAddressLine1}
          shippingAddressLine2={shippingAddressLine2}
          shippingContactName={shippingContactName}
          shippingPhoneNumber={shippingPhoneNumber}
          shippingCity={shippingCity}
          shippingState={shippingState}
          shippingZipCode={shippingZipCode}
          specialInstructions={specialInstructions}
          itemsSubtotal={itemsSubtotal}
          shipBy={shipBy}
          grandTotal={grandTotal}
          orderDate={orderDate}
          companyDiscount={companyDiscount}
          companyName={companyName}
          orderItems={orderItems}
          onChangeLoading={(loading) => setLoading(loading)}
          onChangeFailedToLoad={(failedToLoad) => setFailedToLoad(failedToLoad)}
          onChangeErrorMessage={() => {
            /* Do nothing. */
          }}
        />
      </div>
    </div>
  );
}
interface ResponseBody {
  shippingCountry: string;
  shippingAdministrativeArea: string;
  shippingLocality: string;
  shippingDependentLocality: string;
  shippingPostalCode: string;
  shippingSortingCode: string;
  shippingAddressLine1: string;
  shippingAddressLine2: string;
  shippingCompanyName: string;
  shippingContactName: string;
  shippingPhoneNumber: string;
  specialInstructions: string;
  discountPercent: string;
  companyName: string;
  shipBy: string;
  poNumber: string;
  orderDate: string;
  grandTotal: string;
  itemsSubtotal: string;
  orderItems: OrderItem[];
}

interface OrderItem {
  partNumber: string;
  partDescription: string;
  paintColor: string;
  impellerTrim: string;
  impellerAngle: string;
  price: string;
  quantity: string;
}
